/**
 * @file `ExpiryWarningBanner` — pre-expiry banner that surfaces when
 *       a learner's engagement TTL is within 5 minutes of expiring.
 *       "Keep alive" posts `/api/engagement/touch` to refresh the
 *       sliding TTL; the new `expiresAt` resets the countdown
 *       in-place.
 * @module @labcraft/lab/ui/components/expiry-warning-banner
 *
 * Per #701 § "Pre-expiry warning":
 *
 *   > banner when `expiresAt - now < 5 min` with "Keep alive" button
 *   > that posts engagement-touch (refreshes TTL). Without this, a
 *   > learner reading prose at the 6d 23h mark gets reaped without
 *   > warning.
 *
 * Rendering policy:
 *   • `expiresAt === null` (no lifecycle frame yet) → no banner.
 *   • `expiresAt - now > 5min`                       → no banner.
 *   • `0 < expiresAt - now <= 5min`                  → "Lab expires in
 *                                                      Nm Ns. [Keep alive]"
 *   • `expiresAt - now <= 0`                         → no banner
 *                                                      (lifecycle state
 *                                                      will flip to
 *                                                      `expired`; that
 *                                                      overlay owns the
 *                                                      "expired" copy).
 *
 * Re-renders every 5 s while the banner is visible — that resolution
 * is enough to give the learner a sense of urgency without burning
 * cycles. The interval is cleared when the banner hides.
 */

import { useEffect, useState, type JSX } from 'react'
import styles from './expiry_warning_banner.module.css'

/** Warning window — banner visible when `expiresAt - now` is within this. */
const WARNING_WINDOW_MS = 5 * 60 * 1000

/** Countdown refresh tick. Coarse enough to be cheap, fine enough to feel live. */
const TICK_MS = 5_000

/**
 * Props for {@link ExpiryWarningBanner}.
 *
 * @property expiresAt - Engagement-TTL deadline (ms since epoch) or
 *   `null` if not yet known. The banner renders nothing until a real
 *   `expiresAt` lands and the window opens.
 * @property onKeepAlive - Called when the learner clicks "Keep alive".
 *   The handler should post `/api/engagement/touch` and update the
 *   `expiresAt` prop with the returned deadline.
 */
export type ExpiryWarningBannerProps = {
  expiresAt: number | null
  onKeepAlive: () => void
}

/** Format the remaining duration as "Nm Ns" or "Ns" when under a minute. */
function formatRemaining(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000))
  if (totalSec < 60) return `${totalSec}s`
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return `${min}m ${sec}s`
}

export function ExpiryWarningBanner(
  props: ExpiryWarningBannerProps,
): JSX.Element | null {
  const [now, setNow] = useState(Date.now())

  const remaining = props.expiresAt === null ? null : props.expiresAt - now
  const inWindow =
    remaining !== null && remaining > 0 && remaining <= WARNING_WINDOW_MS

  // Only tick while the banner is actually rendered — no work otherwise.
  useEffect(() => {
    if (!inWindow) return
    const id = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [inWindow])

  if (!inWindow || remaining === null) return null

  return (
    <div className={styles.banner} role="status" aria-live="polite">
      <span className={styles.label}>
        Lab expires in {formatRemaining(remaining)}
      </span>
      <button
        className={styles.button}
        type="button"
        onClick={props.onKeepAlive}>
        Keep alive
      </button>
    </div>
  )
}
